import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, List, Typography } from '@material-ui/core';
import { useSearchStore } from '@services/stores/SearchStore';
import { SearchResultBtn, SearchResultProduct, SearchSuggestion } from '../parts';
import SearchResultContentItem from '@components/header/search/parts/SearchResultContentItem';
import { useTranslation } from 'tools/i18n';
import useApplicationStore from '@services/stores/ApplicationStore';
import useSessionStore from '@services/stores/SessionStore';
import theme from '@components/theme';

const useStyles = makeStyles({
    search__wrapper: {
        padding: '0',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        maxHeight: 'calc(80vh - var(--inner-header-height))',
        overflowY: 'auto',
        zIndex: 100,
        width: '100%',
        '&.focus': {
            //@ts-ignore
            [theme.breakpoints.down('sm')]: {
                'body:has(&)': {
                    overflow: 'hidden',
                },
            },
        },
    },

    autocomplete: {
        display: 'none',
        zIndex: 100,
        width: '100%',
        overflow: 'auto',
        overflowX: 'hidden',
        maxWidth: '100%',
        margin: '0',
        background:'#fff',
        padding: '1em max(15px, calc((100vw - 1600px) / 2)) 2em',
        maxHeight: '65vh',
        '&.glued': {
            boxShadow: 'none',
            borderRadius: 0
        },
        '&.focus': {
            display: 'block'
        },
    },
    resultsContainer: {
    },
    resultsContainerDivider: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap'
    },
    suggestion: {
        width: 'auto',
        padding: '4px 0',
        marginRight: 15,
        letterSpacing: '.15rem',
        opacity: 0,
        '&.focus': {
            transform: 'translateX(0)',
            opacity: 1,
            letterSpacing: '0'
        },
        '& a': {
            display: 'flex',
            alignItems: 'center',
            fontWeight: 400,
            color: 'inherit',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            '&:hover': {
                textDecoration: 'underline',
            },
        },
        '& .searchIcon': {
            marginRight: 8,
            flexShrink: 0,
            color: '#000',
        }
    },
    suggestions__wrapper: {
        paddingBottom: 10,
        marginBottom: 15,
        //@ts-ignore
        [theme.breakpoints.down('xs')]: {
            paddingBottom: 0,
            border: `none`,
        },
        '& ul': {
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8
        },
        //@ts-ignore
        [theme.breakpoints.down('sm')]: {
            marginBottom: 0,
        },
        //@ts-ignore
        [theme.breakpoints.down('sm')]: {
            '& li:nth-of-type(n + 6)': {
                display: 'none'
            }
        }
    },
    productSuggestion__link: {
        boxShadow: 'none'
    },
    products__wrapper: {
        marginBottom: '0.5em',
        '&:last-of-type': {
            borderBottom: 'none',
            paddingBottom: 0,
        },
        //@ts-ignore
        '&.content:before': {
            //@ts-ignore
            [theme.breakpoints.down('xs')]: {
                content: '""',
                width: '90%',
                height: 1,
                background: '#eee',
                margin: '15px auto'
            },
        }
    },
    product: {
        display: 'flex',
        alignItems: 'center',
        padding: 0,
        textDecoration: 'none',
        opacity: 0,
        backgroundColor: theme.palette.primary.background,
        '&.focus': {
            opacity: 1,
        },
        '&:hover': {
            backgroundColor: theme.palette.primary.backgroundContrast
        },
        //@ts-ignore
        [theme.breakpoints.down('sm')]: {
            '&.focus': {
                transitionDelay: '.5s'
            }
        }
    },
    product__image: {
        position: 'relative',
        width: 90,
        marginRight: 10,
        overflow: 'hidden',
        '& > div': {
            display: 'block !important'
        },
        '&.inspiration': {
            width: '90px',
            display: 'block',
            aspectRatio: 1,
            '& img': {
                objectFit: 'cover',
                objectPosition: 'center 30%'
            },
        },
        '&:after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            mixBlendMode: 'multiply',
            boxShadow: 'inset 0 0 90px 8px rgb(240, 240, 240, 0.75)'
        }
    },
    product__info: {
        display: 'flex',
        alignSelf: 'center',
        justifyContent: 'space-between',
        flexDirection: 'column',
        width: '100%',
        padding: '5px',
    },
    product__name: {
        fontSize: 14,
        fontWeight: 300,
        color: theme.palette.primary.text
    },
    product__prices: {
        display: 'flex'
    },
    product__price: {
        fontSize: 13,
        color: '#333',
        '&.special': {
            color: theme.palette.primary.special
        },
        '&.old': {
            marginLeft: 10,
            textDecoration: 'line-through',
            color: theme.palette.primary.disabled
        }
    },
    product__type: {
        fontSize: 14,
        fontWeight: 300,
        fontStyle: 'italic',
        color: theme.palette.primary.text,
        textTransform: 'capitalize',
        '&.desktop': {
            display: 'none'
        },
        //@ts-ignore
        [theme.breakpoints.up('sm')]: {
            '&.desktop': {
                display: 'block',
                fontStyle: 'unset',
                fontWeight: 500,
                marginBottom: '5px',
            },
            '&.mobile': {
                display: 'none'
            }
        }
    },
    showMoreItem: {
        position: 'sticky',
        bottom: '-2em',
        padding: '1em 0',
        background: '#fff',
    },
    product__showMore: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        ...theme.button.purchase,
        margin: 0,
        padding: '10px 15px',
        backgroundColor: theme.palette.primary.purchase.background,
        fontSize: 12,
        textDecoration: 'none',
        '&:hover': {
            backgroundColor: theme.palette.primary.purchase.hover,
        },
        '.xmas &': {
            backgroundColor: theme?.palette?.primary?.main
        }
    },
    noResultsText: {
        fontSize: 14,
    },
    relatedSearches: {
        margin: '0 0 10px',
        fontWeight: 500,
        fontSize: 14
    },
}, {name: 'SearchResultsDropdown'});

export default function SearchResultsDropdown(){
    const classes = useStyles();
    const { t } = useTranslation();
    const {
        query,
        products,
        content,
        suggestions,
        hitCount,
        suggestedItems,
        dirty,
        loadSuggestions,
        getFallbackSuggestions
    } = useSearchStore();

    const { config, salesChannel } = useSessionStore((state) => state.session);
    const focus = useApplicationStore((state) => state.modals.searchOpen);

    useEffect(() => {
        if (focus && !dirty && suggestedItems.length === 0) {
            loadSuggestions();
        }
    }, [focus, dirty, loadSuggestions, suggestedItems.length]);

    const wrapperClasses = [
        classes.search__wrapper,
        focus ? 'focus' : '',
        dirty ? 'dirty' : ''
    ].join(' ');

    const autocompleteClasses = [
        classes.autocomplete,
        focus ? 'focus' : ''
    ].join(' ');

    // @ts-ignore
    return (
        <div className={wrapperClasses}>
            <div className={autocompleteClasses}>
                <Grid container>
                    <Grid xs={12} className={classes.suggestions__wrapper} item>
                        {suggestions.length > 0 ? (
                            <List disablePadding>
                                {suggestions.map((suggestion, index) => (
                                    <SearchSuggestion
                                        key={index}
                                        props={{ suggestion, index, query, classes, t, focus }}
                                    />
                                ))}
                            </List>
                        ) : (
                            <>
                                <Typography className={classes.relatedSearches} variant="body1">
                                    {t('search-popular-searches')}
                                </Typography>
                                <List disablePadding>
                                    {getFallbackSuggestions(salesChannel).map(
                                        (suggestion: any, index: React.Key | null | undefined) => (
                                            <SearchSuggestion
                                                key={index}
                                                props={{ suggestion, index, query, classes, t, focus }}
                                            />
                                        ),
                                    )}
                                </List>
                            </>
                        )}
                    </Grid>

                    <Grid className={classes.resultsContainer} container>
                        <Grid xs={12} className={classes.resultsContainerDivider} item>
                            {products.length > 0 ? (
                                <Grid xs={12} sm={6} md={6} lg={6} className={classes.products__wrapper} spacing={1} container>
                                    {products.map((product, index) => (
                                        <SearchResultProduct
                                            key={product.id}
                                            props={{ product, index, classes, focus }}
                                        />
                                    ))}
                                </Grid>
                            ) : query.length > 2 && content.length === 0 ? (
                                <Typography className={classes.noResultsText} variant="body1">
                                    {t('search-result-no-hit')}
                                </Typography>
                            ) : suggestedItems.length > 0 && content.length === 0 ? (
                                <Grid className={classes.products__wrapper} spacing={1} container>
                                    {suggestedItems.map((product, index) => (
                                        <SearchResultProduct
                                            key={product.id}
                                            props={{ product, index, classes, focus }}
                                        />
                                    ))}
                                </Grid>
                            ) : null}

                            {content.length > 0 && (
                                <Grid xs={12} sm={6} md={6} lg={6} className={[classes.products__wrapper, 'content'].join(' ')} spacing={1} container>
                                    {content.map((item, index) => (
                                        <SearchResultContentItem
                                            key={item?.key}
                                            props={{ classes, item, source: 'dropdown', index }}
                                        />
                                    ))}
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    {hitCount > 6 && query && (
                        <Grid xs={12} className={classes.showMoreItem} item>
                            <SearchResultBtn query={query} classes={classes} />
                        </Grid>
                    )}
                </Grid>
            </div>
        </div>
    );
};